import type { StreamingState, StreamingAction } from "./types";
import { GAP_DURATION } from "./types";

// AIDEV-NOTE: Initial state - all flags false, phase idle
// AIDEV-NOTE: introUrl/mainUrl removed - both intro and main now stream as chunks
export const initialState: StreamingState = {
    phase: "idle",
    isPlaying: false,
    isLoading: false,
    isGenerating: false,
    isReconnecting: false,
    error: null,
    billingBlocked: null,
    introDuration: 0,
    mainDuration: 0,
    currentTime: 0,
    introAudioReady: false,
    mainAudioReady: false,
    source: null,
    slowNetworkDetected: false,
    needsUserGesture: false,
    isStalled: false,
};

/**
 * Pure reducer for streaming meditation state machine.
 *
 * AIDEV-NOTE: All state transitions are explicit here, making the state machine
 * auditable and testable. This eliminates scattered transition logic and makes
 * it easy to verify correctness.
 *
 * State machine flow (updated for streaming intro):
 * idle -> (START) -> idle+loading
 * idle+loading -> (INTRO_CHUNK_RECEIVED) -> idle+loading+generating (first intro chunk)
 * idle+loading -> (INTRO_READY) -> intro audio ready to play
 * intro -> (INTRO_COMPLETE) -> intro duration known
 * intro -> (INTRO_PLAYBACK_STARTED) -> intro+playing
 * intro -> (INTRO_ENDED) -> gap
 * gap -> (GAP_ENDED) -> main (if mainAudioReady) | gap (if not ready)
 * main -> (MAIN_ENDED) -> complete
 * any -> (STOP) -> idle
 * any -> (ERROR) -> idle+error (terminal; Retry runs a fresh start)
 */
export function reducer(
    state: StreamingState,
    action: StreamingAction,
): StreamingState {
    switch (action.type) {
        case "START":
            return {
                ...initialState,
                isLoading: true,
            };

        case "INTRO_CHUNK_RECEIVED":
            // First intro chunk starts generating state
            return {
                ...state,
                isLoading: false,
                isGenerating: true,
            };

        case "INTRO_READY":
            return {
                ...state,
                introAudioReady: true,
            };

        case "INTRO_COMPLETE":
            return {
                ...state,
                introDuration: action.duration,
                introAudioReady: true,
            };

        case "INTRO_PLAYBACK_STARTED":
            return {
                ...state,
                phase: "intro",
                isPlaying: true,
            };

        case "CHUNK_RECEIVED":
            // AIDEV-NOTE: First main chunk also clears isLoading for input streaming
            // where there's no intro (INTRO_CHUNK_RECEIVED never fires)
            return {
                ...state,
                isLoading: false,
                isGenerating: true,
            };

        case "MAIN_READY":
            return {
                ...state,
                mainAudioReady: true,
            };

        case "GENERATION_COMPLETE": {
            // AIDEV-NOTE: Calculate main duration from total - intro - gap
            const mainDuration =
                action.totalDuration - state.introDuration - GAP_DURATION;
            const nextState = {
                ...state,
                mainDuration: mainDuration > 0 ? mainDuration : 0,
                isGenerating: false,
                isLoading: false,
                mainAudioReady: true,
            };
            return {
                ...nextState,
                currentTime: clampKnownPlaybackTime(
                    nextState,
                    state.currentTime,
                ),
            };
        }

        case "INTRO_ENDED":
            return {
                ...state,
                phase: "gap",
                isPlaying: false,
                isStalled: false,
            };

        case "GAP_ENDED":
            // Only transition to main if main audio is ready
            if (state.mainAudioReady) {
                return {
                    ...state,
                    phase: "main",
                };
            }
            // Stay in gap, waiting for main audio
            return state;

        case "MAIN_PLAYBACK_STARTED":
            // AIDEV-NOTE: Successful playback restart clears the tap-to-listen
            // recovery state so fullscreen can switch back to normal playback UI.
            return {
                ...state,
                phase: "main",
                isPlaying: true,
                needsUserGesture: false,
            };

        case "MAIN_ENDED":
            // AIDEV-NOTE: Clear isGenerating here. If main audio reached its
            // natural end, generation must have finished — otherwise the
            // audio element couldn't have rendered the full track. Observed
            // on staging 2026-04-14: the SSE `complete` event can fail to
            // arrive on the native-HLS playback path, leaving isGenerating
            // stuck true and the UI spinner orphaned forever. This is safe
            // because MAIN_ENDED only fires post-playback — unlike the
            // startup/buffering logic at audio-controller.ts:1633 which
            // uses `!isGenerating && hasFinalMainDuration` for different
            // decisions before playback starts.
            return {
                ...state,
                phase: "complete",
                isPlaying: false,
                isGenerating: false,
                // AIDEV-NOTE: A stall indicator must not outlive playback.
                isStalled: false,
            };

        case "PLAY":
            return {
                ...state,
                isPlaying: true,
            };

        case "PAUSE":
            // AIDEV-NOTE: A paused element can't be buffering — clear the spinner.
            return {
                ...state,
                isPlaying: false,
                isStalled: false,
            };

        case "STOP":
            return initialState;

        case "RECONNECTING":
            return {
                ...state,
                isReconnecting: true,
                error: null,
            };

        case "RECONNECT_SUCCESS":
            return {
                ...state,
                isReconnecting: false,
                isPlaying: true,
            };

        case "RECONNECT_FAILED":
            // AIDEV-NOTE: EVO-1815 — reconnect failures are terminal. Reset the
            // playback machine to idle (audio is already stopped by the hook) so
            // start() doesn't see an "active meditation" and ignore Retry taps.
            return {
                ...state,
                phase: "idle",
                isReconnecting: false,
                error: action.message,
                isPlaying: false,
                isLoading: false,
                isGenerating: false,
                isStalled: false,
                needsUserGesture: false,
            };

        case "SEEK":
            return {
                ...state,
                currentTime: clampKnownPlaybackTime(state, action.time),
            };

        case "TIME_UPDATE":
            return {
                ...state,
                currentTime: clampKnownPlaybackTime(state, action.time),
            };

        case "ERROR":
            // AIDEV-NOTE: Ensure error is always truthy to trigger error UI display.
            // Empty/falsy messages would keep breathing animation showing instead of error state.
            // AIDEV-NOTE: EVO-1815 — ERROR is terminal: reset phase to idle so the
            // Retry button's start() isn't blocked by the double-start guard.
            // Audio teardown happens before this dispatch (failPlayback / the
            // hook's dispatchTerminalPlaybackError both stop the controller).
            // Durations/currentTime are kept for end-of-playback telemetry.
            return {
                ...state,
                phase: "idle",
                isPlaying: false,
                isReconnecting: false,
                isStalled: false,
                needsUserGesture: false,
                error: action.message || "An error occurred",
                billingBlocked: action.billingBlocked ?? null,
                isLoading: false,
                isGenerating: false,
            };

        // AIDEV-NOTE: Browser-native duration discovery for iOS Safari fallback mode.
        // Safari fires durationchange when progressive download completes and duration is known.
        case "DURATION_DISCOVERED":
            const nextState = {
                ...state,
                mainDuration: action.duration,
                isGenerating: false,
                isLoading: false,
                mainAudioReady: true,
            };
            return {
                ...nextState,
                currentTime: clampKnownPlaybackTime(
                    nextState,
                    state.currentTime,
                ),
            };

        case "SET_SOURCE":
            return {
                ...state,
                source: action.source,
            };

        case "SLOW_NETWORK_DETECTED":
            return {
                ...state,
                slowNetworkDetected: true,
            };

        // AIDEV-NOTE: EVO-1813 — debounced buffering stall indicator. Set/cleared
        // by AudioController's StallDetector; an overlay flag, not a phase change.
        case "STALL_DETECTED":
            return {
                ...state,
                isStalled: true,
            };

        case "STALL_CLEARED":
            return {
                ...state,
                isStalled: false,
            };

        // AIDEV-NOTE: iOS autoplay policy rejected play() because the user
        // gesture expired during HLS buffering. Keep all audio state intact
        // (buffered data is still valid) and signal the UI to show a
        // tap-to-listen button for a fresh gesture.
        case "NEEDS_USER_GESTURE":
            return {
                ...state,
                needsUserGesture: true,
                isPlaying: false,
                isStalled: false,
            };

        default:
            return state;
    }
}

function clampKnownPlaybackTime(state: StreamingState, time: number) {
    const totalDuration = getTotalDuration(state);
    if (!Number.isFinite(totalDuration) || totalDuration <= 0) {
        return time;
    }

    // AIDEV-NOTE: Native HLS clocks can drift past the finalized meditation
    // duration by a couple seconds near ENDLIST; clamp once duration is known.
    return Math.min(time, totalDuration);
}

/**
 * Helper to get status label for UI display
 */
export function getStatusLabel(state: StreamingState): string {
    if (state.isReconnecting) return "Reconnecting...";
    if (state.error) return "Error";
    if (state.isLoading) return "Connecting...";
    if (state.isGenerating && state.phase !== "intro" && state.phase !== "main")
        return "Generating...";

    switch (state.phase) {
        case "intro":
            return "Settling in...";
        case "gap":
            return "Transitioning...";
        case "main":
            return "Meditation";
        case "complete":
            return "Complete";
        default:
            return "";
    }
}

/**
 * Helper to calculate total duration
 */
export function getTotalDuration(state: StreamingState): number {
    // AIDEV-NOTE: When there's no intro (URL playback for cached files),
    // total duration is just the main duration — no intro/gap offset.
    const hasIntro = state.introDuration > 0;
    const offset = hasIntro ? state.introDuration + GAP_DURATION : 0;

    if (state.mainDuration > 0) {
        return offset + state.mainDuration;
    }
    // Estimate if main duration not known yet
    if (hasIntro) {
        return offset + state.introDuration * 10;
    }
    return 0;
}
